import Swiper from 'swiper'
// import { Navigation, Pagination } from 'swiper/modules'
import { Scrollbar } from 'swiper/modules'
import { imagesLoaded, handle, sleep } from 'utils/gw'
import { formatPrice } from 'utils/general'
import { isBP, onBP } from 'utils/dom'
import { lowPriority } from 'lib/performance'
import { NostoHelper } from 'helpers/nosto'

if (!customElements.get('dynamic-products')) {
  class DynamicProducts extends HTMLElement {
    constructor() {
      super()
      this.productSource = this.getAttribute('product-source')
      this.heading = this.querySelector('[data-heading]') ?? this.closest('accordion-item')?.querySelector('[data-heading]')
      this.isCartDrawer = !!this.closest('cart-drawer')
      this.productCards = []
      this.trigger = 'init'
      this.cardTemplate = this.querySelector('template[data-card-template]')?.content?.firstElementChild

      const fallbackTemplateEl = this.querySelector('template[data-fallback-collection]') ?? document.querySelector(`template[data-fallback-collection="${this.getAttribute('data-fallback-template')}"]`)
      this.fallbackTemplate = fallbackTemplateEl?.content
      this.fallbackCollectionTitle = fallbackTemplateEl?.dataset.fallbackCollection
      this.cacheRendered = false
      this.nostoSlotId = this.getAttribute('nosto-slot-id')
      this.productLinkParams = {}
      this.isNosto = false
      this.viewEventSent = document.documentElement.dataset.pageType === 'product'
      this.hasPromoProduct = this.getAttribute('data-promo-product') === 'true'

      this.slidesPerView = {
        0: this.isCartDrawer ? 3.9 : 2,
        500: this.isCartDrawer ? 5 : 3,
        750: this.isCartDrawer ? 5 : 3,
        990: this.isCartDrawer ? 5 : 4,
        1200: this.isCartDrawer ? 5 : 4,
      }
      this.navigationElements = [...this.querySelectorAll('.swiper-button, .swiper-pagination')]

      this.patterns = {
        arrayIndex: /\[([0-9]+)\]/,
      }

      this.init()
    }

    connectedCallback() {
      this.bindEvents()
    }

    disconnectedCallback() {
      this.unbindEvents()
    }

    get slideCount() {
      return [...this.querySelectorAll('.swiper-slide')].length
    }

    bindEvents() {
      const callback = () => {
        this.style.setProperty('--slides-per-view', this.currentSlidesPerView)
        this.initSlider()
      }

      this.bpUnsubscribers = []
      for (const bp of Object.keys(this.slidesPerView)) {
        if (bp === '0') continue
        this.bpUnsubscribers.push(onBP(bp, callback))
      }

      document.addEventListener('CART_STATE_CHANGED', this.onCartStateChanged.bind(this))
    }

    unbindEvents() {
      for (const unsubscriber of this.bpUnsubscribers) {
        unsubscriber()
      }
    }

    get hasProducts() {
      return this.wrapper.childNodes.length > 0
    }

    async init() {
      this.renderFromCache()

      let products = []
      switch (this.productSource) {
        case 'favorites':
          products = await this.getFavorites()
          break
        case 'recent':
          products = await this.getRecentlyViewedItems()
          break
        case 'recommendations':
          products = await this.getNostoProductRecommendations()
          break
      }

      if (this.productSource !== 'recommendations') {
        this.renderProducts(products)
      }
    }

    async onCartStateChanged() {
      console.debug('Dynamic Products: Cart state changed', this.productSource)
      setTimeout(() => {
        this.togglePromoProducts()
      }, 10)
    }

    togglePromoProducts() {
      if (!this.promoConfig) return

      for (const promoProduct of this.promoConfig) {
        const productElement = this.wrapper.querySelector(`[data-product-id="${promoProduct.product_id}"]`)

        if (promoProduct.is_added_to_cart) {
          productElement.classList.add('hidden-promo-product')
        } else {
          productElement.classList.remove('hidden-promo-product')
        }

        console.log('Dynamic Products: Promo product element', productElement)
      }
    }

    renderProducts(products) {
      if (!products.length) {
        this.renderFallback()
        return
      }

      const prepareProductCallback = this.isNosto
        ? this.prepareNostoProductResult.bind(this)
        : this.prepareProductResult.bind(this)

      products = products.map(prepareProductCallback)
      this.products = products

      if (this.hasProducts) this.initSliderOnIntersection()

      if (this.products?.length) {
        this.show()
        this.loopProducts(this.products)
      } else {
        this.hide()
      }
    }

    renderFallback() {
      console.debug('Dynamic Products: Rendering fallback...', this.fallbackCollectionTitle)
      if (!this.fallbackTemplate) {
        this.hide()
        return
      }

      const fallback = this.fallbackTemplate.cloneNode(true)
      this.wrapper.replaceChildren(...fallback.children)
      NostoHelper.triggerMigration(`#${this.id}`, true)
      this.heading.textContent = this.fallbackCollectionTitle
      this.initSliderOnIntersection()
      this.show()
    }

    show() {
      this.style.display = 'block'
      this.active = true
      this.dispatchEvent(new CustomEvent('dynamic-products:initialized', { detail: { active: true, element: this } }))
    }

    hide() {
      this.style.display = 'none'
      this.active = false
      this.dispatchEvent(new CustomEvent('dynamic-products:initialized', { detail: { active: false, element: this } }))
    }

    set active(value) {
      this.dataset.active = value
    }

    get active() {
      return this.dataset.active
    }

    // get enabled() {
    //   return this.hasProducts && this.style.display !== 'none'
    // }

    async connectToAccounts(retries = 5) {
      this.accountsApp ??= document.querySelector('gw-customer-accounts')
      this.accountDashboard ??= this.accountsApp.shadowRoot?.querySelector('gw-customer-accounts-dashboard')

      if (!this.accountsApp || !this.accountDashboard) {
        console.debug('Dynamic Products: Failed to connect to GW Customer Accounts')

        if (retries > 0) {
          await sleep(300)
          console.debug('Dynamic Products: Retrying connection to GW Customer Accounts')
          return await this.connectToAccounts(retries - 1)
        }

        console.debug('Dynamic Products: Aborted')
        return false
      }

      return true
    }

    async getFavorites() {
      const connected = await this.connectToAccounts()
      if (!connected) return []

      const favorites = await this.accountDashboard._getFavoriteItems()
      console.debug('Dynamic Products: Favourites retrieved', favorites)

      return favorites
    }

    async getRecentlyViewedItems() {
      const connected = await this.connectToAccounts()
      if (!connected) return []

      const recentlyViewedItems = await this.accountDashboard._getRecentlyViewedItems()
      console.debug('Dynamic Products: Recently viewed items retrieved', recentlyViewedItems)

      return recentlyViewedItems
    }

    async getNostoProductRecommendations(retries = 5) {
      this.nostoPlacementId = this.getAttribute('nosto-placement-id')
      if (!this.nostoPlacementId) {
        console.error('Dynamic Products: No Nosto placement ID found')
        return []
      }

      if (typeof window.nostojs !== 'function') {
        if (retries > 0) {
          await sleep(300)
          return await this.getNostoProductRecommendations(retries - 1)
        }

        console.debug('Dynamic Products: Product recommendations not found')
        return []
      }

      this.isNosto = true
      let products = []
      await nostojs(async api => {
        const sourceProductId = this.getAttribute('product-id')

        let session = api.defaultSession()
        session = (sourceProductId && !this.viewEventSent) ? session.viewProduct(sourceProductId) : session.viewOther()
        this.viewEventSent = true
        const response = await session.setPlacements(this.nostoPlacementId ? [this.nostoPlacementId] : api.placements.getPlacements()).update()
        products = response.recommendations[this.nostoPlacementId].products
        this.nostoSlotId = response.recommendations[this.nostoPlacementId].result_id
        this.productLinkParams.nosto = this.nostoSlotId

        if (this.hasPromoProduct) {
          const promoProducts = this.promoConfig

          if (promoProducts) {
            // Remove the products that are already in the promo products array
            products = products.filter(product => !promoProducts.some(promoProduct => promoProduct.product_id == product.product_id))

            // Add the promo products that are not added to the cart
            products.unshift(...promoProducts)
          }
        }

        this.renderProducts(products)

        setTimeout(() => {
          this.togglePromoProducts()
        }, 10)
      })
    }

    get wrapper() {
      this.sliderWrapper = this.sliderWrapper ?? this.querySelector(`.swiper-wrapper`)

      return this.sliderWrapper
    }

    prepareProductResult(product) {
      if (product.prepared) return product

      product.product_id = product.id
      product.url = `https://${Shopify.shop}/products/${product.handle}`
      product.image_url = product.featuredImage.url
      product.image_alt = product.featuredImage.altText ?? product.featuredImage.alt
      delete product.featuredImage

      if (product.variantId) {
        product.variant_id = product.variantId
        delete product.variantId
      }

      if (product.priceRangeV2) {
        product.price = formatPrice(product.priceRangeV2.minVariantPrice.amount * 100)
        delete product.priceRangeV2
      } else if (product.variants?.nodes?.length) {
        product.price = formatPrice(product.variants.nodes[0].price)
        delete product.variants
      }

      product.prepared = true

      return product
    }

    get productLinkParamString() {
      return `?${Object.entries(this.productLinkParams).map(([key, value]) => `${key}=${value}`).join('&')}`
    }

    prepareNostoProductResult(product) {
      if (product.prepared) return product

      product.title = product.name
      delete product.name

      product.tags = [...product.tags1, ...product.tags2, ...product.tags3]
      delete product.tags1
      delete product.tags2
      delete product.tags3

      product.handle = handle(product.title)
      product.variants = product.skus.map(variant => {
        const [size, color] = variant.name.split('|').map(part => part.trim())
        variant.size = size
        variant.color = color
        return variant
      })
      delete product.skus

      product.variant_id = product.variants.find(variant => variant.available).id

      product.vendor = product.brand
      delete product.brand

      product.collections = product.categories
      delete product.categories

      if (product.price < product.list_price) {
        product.on_sale = true
        // TODO: Handle price localization
        // product.price = formatPrice(product.price * 100)
        // product.list_price = formatPrice(product.list_price * 100)
      }

      const currency = window.Shopify?.currency
      // if (currency) {
      //   const rate = parseFloat(currency.rate)
      //   product.price = product.price * rate
      //   product.list_price = product.list_price * rate
      // product.price_currency_code = currency.active
      // }

      product.price = formatPrice(product.price * 100)
      product.list_price = formatPrice(product.list_price * 100)

      if (Shopify?.money_trailing_zeros === false) {
        product.price = product.price.replace('.00', '')
        product.list_price = product.list_price.replace('.00', '')
      }

      if (currency?.active != 'USD') {
        product.price = `${product.price_currency_code} ${product.price}`
        product.list_price = `${product.price_currency_code} ${product.list_price}`
      }

      product.prepared = true

      return product
    }

    productCardMarkup(product) {
      const productCard = this.cardTemplate.cloneNode(true)

      const conditionalElements = [...productCard.querySelectorAll('[data-if]')].filter(
        el => !el.closest('[data-template="loop"]')
      )

      for (const element of conditionalElements) {
        let condition = element.dataset.if
        const inverse = condition.startsWith('!')
        let index
        if (inverse) {
          condition = condition.replace('!', '')
        }
        // TODO: Review this
        const conditionKeyMatch = condition.match(this.patterns.arrayIndex)
        if (conditionKeyMatch) {
          condition = condition.replace(conditionKeyMatch[0], '')
          index = conditionKeyMatch[1]
        }

        let isConditionMet = false

        if (
          (typeof index === 'undefined' && product[condition]) ||
          (typeof index !== 'undefined' && product[condition]?.[index])
        ) {
          isConditionMet = true
        }

        isConditionMet = inverse ? !isConditionMet : isConditionMet

        if (!isConditionMet) {
          element.remove()
        }
      }

      let cardContentMarkup = productCard.innerHTML

      for (const key of Object.keys(product)) {
        const replace = product[key]

        const search = `[[${key}]]`
        if (Array.isArray(replace)) {
          for (const index in replace) {
            const itemReplace = replace[index]

            if (!['string', 'number'].includes(typeof itemReplace)) continue

            const itemSearch = search.replace(']]', `[${index}]]]`)
            cardContentMarkup = cardContentMarkup.replaceAll(itemSearch, itemReplace)
          }

          continue
        }

        if (!['string', 'number'].includes(typeof replace)) continue

        cardContentMarkup = cardContentMarkup.replaceAll(search, replace)
      }

      productCard.innerHTML = cardContentMarkup

      const replacementElements = [...productCard.querySelectorAll('[data-template-element]')]
      for (const el of replacementElements) {
        const elementType = el.dataset.templateElement
        const element = document.createElement(elementType)
        for (const attr of el.attributes) {
          element.setAttribute(attr.name, attr.value)
        }
        element.innerHTML = el.innerHTML
        el.replaceWith(element)
      }

      const conditionalClassElements = [...productCard.querySelectorAll('[data-conditional-classes]')]
      for (const el of conditionalClassElements) {
        const conditionalClasses = JSON.parse(el.dataset.conditionalClasses)

        for (let [condition, classes] of Object.entries(conditionalClasses)) {
          if (!Array.isArray(classes)) classes = [classes]
          const inverse = condition.startsWith('!')
          let index
          if (inverse) {
            condition = condition.replace('!', '')
          }
          const conditionKeyMatch = condition.match(this.patterns.arrayIndex)
          if (conditionKeyMatch) {
            condition = condition.replace(conditionKeyMatch[0], '')
            index = conditionKeyMatch[1]
          }

          let isConditionMet = false

          if (
            (typeof index === 'undefined' && product[condition]) ||
            (typeof index !== 'undefined' && product[condition]?.[index])
          ) {
            isConditionMet = true
          }

          isConditionMet = inverse ? !isConditionMet : isConditionMet

          if (isConditionMet) {
            el.classList.add(...classes)
          }
        }
      }

      return productCard
    }

    // this.cacheRendered
    async loadFullProductCard(slide, url) {
      url = url.replace('https://brochuwalker.myshopify.com', 'https://www.brochuwalker.com');

      try {
        const response = await fetch(`${url}?section_id=api-product-card-no-colors`)
        if (!response.ok) return

        const product = await response.text()
        const fresh = document.createElement('div')
        fresh.innerHTML = product

        await imagesLoaded(fresh, { container: true })

        const card = fresh.querySelector('[data-api-content]').firstElementChild
        const cardLink = card.querySelector('a')
        cardLink.setAttribute('href', `${cardLink.getAttribute('href')}${this.productLinkParamString}`)

        return card
        // slide.replaceChildren(fresh.querySelector('[data-api-content]'))
      } catch (error) {
        console.error(`Error loading full product card from ${url}`, error)
      }
    }

    async loopProducts(products, count = 0, shown = 0) {
      count = count ? count : products.length - 1
      if (shown == 0) {
        this.productCards = []
        // if (!this.hasProducts) {
        //   this.wrapper.style.display = 'none'
        //   this.wrapper.style.opacity = 0
        // }
      }
      if (shown <= count) {
        const product = products[shown]

        const slide = document.createElement('div')
        slide.classList.add('swiper-slide')

        if (this.cacheRendered) {
          const slideContent = await this.loadFullProductCard(slide, product.url)
          slide.appendChild(slideContent)
        } else {
          slide.appendChild(this.productCardMarkup(product))

          // TODO: Maybe remove low priority? Ensure this only triggers once drawer is open (return deferred loading?)
          lowPriority(async () => {
            const slideContent = await this.loadFullProductCard(slide, product.url)
            slide.replaceChildren(slideContent)
            // this.loadFullProductCard(slide, product.url)
          }, 0)
        }

        this.productCards.push(slide)

        shown++
        this.loopProducts(products, count, shown)
      } else {
        await this.render()
      }
    }

    get cacheKey() {
      return `dynamic-products-${this.productSource}-${this.id}`
    }

    get promoConfig() {
      const promoConfig = document.getElementById('cart-drawer-promo-config')
      if (!promoConfig) return null

      return JSON.parse(promoConfig.textContent)
    }

    cache() {
      localStorage.setItem(this.cacheKey, this.wrapper.innerHTML)
    }

    renderFromCache() {
      const cached = localStorage.getItem(this.cacheKey)
      if (!cached) {
        this.cacheRendered = false
        return
      }

      console.debug('Rendering dynamic products from cache', this.id)

      this.wrapper.innerHTML = cached
      this.cacheRendered = true
      this.show()
      this.initSliderOnIntersection()
      setTimeout(() => {
        this.togglePromoProducts()
      }, 10)
    }

    get cached() {
      return localStorage.getItem(this.cacheKey)
    }

    async render() {
      this.wrapper.replaceChildren(...this.productCards)
      NostoHelper.triggerMigration(`#${this.id}`, true)
      this.cache()
      console.debug('Rendering dynamic products', this.id)

      // this.wrapper.style.display = 'inline-flex'
      // if (!this.cacheRendered) {
      //   this.wrapper.animate([{ opacity: 0 }, { opacity: 1 }], {
      //     duration: 300,
      //     fill: 'forwards',
      //     easing: 'ease-out',
      //   })
      // }

      this.initSliderOnIntersection()
      setTimeout(() => {
        this.togglePromoProducts()
      }, 10)
    }

    get currentSlidesPerView() {
      this.bpKeys ??= Object.keys(this.slidesPerView).toReversed()
      for (const bp of this.bpKeys) {
        if (isBP(bp)) return this.slidesPerView[bp]
      }

      // Fallback: if nothing matches, return the default (first) slidesPerView value
      return this.slidesPerView[Object.keys(this.slidesPerView)[0]]
    }

    get hasSlider() {
      return this.slideCount > Math.floor(this.currentSlidesPerView)
    }

    initSliderOnIntersection() {
      this.style.setProperty('--slides-per-view', this.currentSlidesPerView)

      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          this.initSlider()
          observer.disconnect()
        })
      }, {
        threshold: 0.1
      })
      observer.observe(this)
    }

    // TODO: Re-init on resize (breakpoint change)
    initSlider() {
      if (this.slider) {
        this.slider.destroy()
      }

      let options = {
        spaceBetween: 0,
        slidesPerView: this.slidesPerView[0],
        slidesPerGroup: Math.floor(this.slidesPerView[0]),
        breakpoints: {
          500: {
            slidesPerView: this.slidesPerView[500],
            slidesPerGroup: Math.floor(this.slidesPerView[500]),
          },
          750: {
            slidesPerView: this.slidesPerView[750],
            slidesPerGroup: Math.floor(this.slidesPerView[750]),
          },
          990: {
            slidesPerView: this.slidesPerView[990],
            slidesPerGroup: Math.floor(this.slidesPerView[990]),
          },
          1200: {
            slidesPerView: this.slidesPerView[1200],
            slidesPerGroup: Math.floor(this.slidesPerView[1200]),
          },
        },
      }

      if (this.hasSlider) {
        options = {
          ...options,
          modules: [Scrollbar],
          scrollbar: {
            el: this.querySelector('.swiper-scrollbar'),
            draggable: true,
            hide: false,
          },
          // modules: [Navigation, Pagination],
          // navigation: {
          //   nextEl: '.swiper-button-next',
          //   prevEl: '.swiper-button-prev',
          // },
          // pagination: {
          //   el: this.querySelector('.swiper-pagination'),
          //   clickable: true,
          // },
        }
        this.navigationElements.forEach(el => (el.style.display = ''))
      } else {
        this.navigationElements.forEach(el => (el.style.display = 'none'))
      }

      this.slider = new Swiper(this.querySelector('.swiper'), options)
      this.style.opacity = 1
    }
  }

  customElements.define('dynamic-products', DynamicProducts)
}
